// src/stores/EntityStore.ts
// What each tab has in context: the entity its page is showing, plus the
// resources and aspects hanging off it.
//
// Keyed by tab because the workspace keeps several panes mounted at once. With
// one global slot the last page to render won — open a task in one pane and a
// team in the other, and the rail showed whichever effect ran last, not the
// pane being worked in. Pages write under their own tab id (see
// components/tabs/TabContext); the rail reads the tab in front.

import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { resourceStore } from '@core/stores/resource/ResourceStore';
import type { Resource } from '@core/types/resource';
import type { Aspect } from '@core/types/task/Aspect';
import { DETACHED_TAB_ID, tabsStore } from './TabsStore';

export interface EntityContext {
  urn: string;
  type: string;
  id: string;
  title: string;
}

/** Everything one tab has in context. */
export interface EntityTabState {
  currentEntity: EntityContext | null;
  resourceCounts: Record<string, number>;
  aspectCounts: Record<string, number>;
  resources: Resource[];
  aspects: Aspect[];
  isLoadingResources: boolean;
  isLoadingAspects: boolean;
}

function emptyState(): EntityTabState {
  return {
    currentEntity: null,
    resourceCounts: {},
    aspectCounts: {},
    resources: [],
    aspects: [],
    isLoadingResources: false,
    isLoadingAspects: false,
  };
}

/** Read-only stand-in for a tab that has nothing in context yet. */
const EMPTY: EntityTabState = Object.freeze(emptyState());

class EntityStore {
  private states = new Map<string, EntityTabState>();
  private resourceVersions = new Map<string, number>();
  private requestSequence = 0;

  constructor() {
    makeAutoObservable(this);

    // A closed tab's context is dead weight — and worse, it would come back if
    // the id were ever reused.
    reaction(
      () => tabsStore.liveTabIds.join('\n'),
      (ids) => this.prune(new Set(ids.split('\n'))),
    );
  }

  // --- reads -----------------------------------------------------------------

  /** Context of one tab. Missing keys read as empty rather than being created. */
  stateFor(tabId: string): EntityTabState {
    return this.states.get(tabId) ?? EMPTY;
  }

  /** Context of the tab in front — what the rail and the breadcrumb speak for. */
  get active(): EntityTabState {
    return this.stateFor(tabsStore.activeTabId);
  }

  get currentEntity(): EntityContext | null {
    return this.active.currentEntity;
  }

  get resources(): Resource[] {
    return this.active.resources;
  }

  get aspects(): Aspect[] {
    return this.active.aspects;
  }

  get isLoadingResources(): boolean {
    return this.active.isLoadingResources;
  }

  get isLoadingAspects(): boolean {
    return this.active.isLoadingAspects;
  }

  get totalResources(): number {
    return countOf(this.active.resourceCounts);
  }

  get totalAspects(): number {
    return countOf(this.active.aspectCounts);
  }

  totalResourcesOf(tabId: string): number {
    return countOf(this.stateFor(tabId).resourceCounts);
  }

  totalAspectsOf(tabId: string): number {
    return countOf(this.stateFor(tabId).aspectCounts);
  }

  // --- writes ----------------------------------------------------------------

  private ensure(tabId: string): EntityTabState {
    const existing = this.states.get(tabId);
    if (existing) return existing;
    const fresh = emptyState();
    this.states.set(tabId, fresh);
    // Read back: MobX stores an observable copy of what was put in.
    return this.states.get(tabId)!;
  }

  private prune(live: Set<string>) {
    for (const key of Array.from(this.states.keys())) {
      if (key !== DETACHED_TAB_ID && !live.has(key)) { this.states.delete(key); this.resourceVersions.delete(key); }
    }
  }

  loadEntity(tabId: string, entity: EntityContext) {
    const state = this.ensure(tabId);
    state.currentEntity = entity;
    state.resourceCounts = {};
    state.aspectCounts = {};
    state.resources = [];
    state.aspects = [];
  }

  async loadEntityData(tabId: string, entity: EntityContext) {
    this.loadEntity(tabId, entity);
    this.loadResources(tabId, entity);
    this.loadAspects(tabId);
  }

  async loadResources(tabId: string, entity: EntityContext) {
    const version = ++this.requestSequence;
    this.resourceVersions.set(tabId, version);
    this.ensure(tabId).isLoadingResources = true;
    try {
      const result = await resourceStore.ListLinks(entity.type, entity.id);
      runInAction(() => {
        // The tab may have been closed while the request was in flight; a
        // response must not resurrect its context.
        const state = this.states.get(tabId);
        if (!state || this.resourceVersions.get(tabId) !== version || state.currentEntity?.urn !== entity.urn || state.currentEntity?.id !== entity.id) return;
        state.resources = result.resources?.items || [];
        state.resourceCounts = { total: state.resources.length };
      });
    } catch {
      // non-critical
    } finally {
      runInAction(() => {
        const state = this.states.get(tabId);
        if (this.resourceVersions.get(tabId) === version && state?.currentEntity?.urn === entity.urn && state.currentEntity.id === entity.id) state.isLoadingResources = false;
      });
    }
  }

  async loadAspects(tabId: string) {
    const state = this.ensure(tabId);
    state.isLoadingAspects = true;
    try {
      // Aspects come from the domain service response (embedded in entity data).
      // Future: BFF BatchGetAspects call.
      // For now, aspects are populated via setAspects() from detail pages.
    } finally {
      runInAction(() => { state.isLoadingAspects = false; });
    }
  }

  setAspects(tabId: string, aspects: Aspect[]) {
    const state = this.ensure(tabId);
    state.aspects = aspects;
    state.aspectCounts = { total: aspects.length };
  }

  clearContext(tabId: string) {
    this.states.delete(tabId);
    this.resourceVersions.delete(tabId);
  }

  setResourceCounts(tabId: string, counts: Record<string, number>) {
    this.ensure(tabId).resourceCounts = counts;
  }

  setAspectCounts(tabId: string, counts: Record<string, number>) {
    this.ensure(tabId).aspectCounts = counts;
  }

  // --- optimistic resource operations ----------------------------------------

  addResourceOptimistic(tabId: string, resource: Resource) {
    const state = this.ensure(tabId);
    state.resources = [...state.resources, resource];
    state.resourceCounts = { total: state.resources.length };
  }

  removeResourceOptimistic(tabId: string, id: string) {
    const state = this.ensure(tabId);
    state.resources = state.resources.filter((r) => r.entity?.id !== id);
    state.resourceCounts = { total: state.resources.length };
  }
}

function countOf(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, c) => sum + c, 0);
}

export const entityStore = new EntityStore();
